import { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../lib/api';
import Button from './ui/Button';
import Input from './ui/Input';
import Textarea from './ui/Textarea';
import Select from './ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import Modal from './ui/Modal';

type ContractRow = {
  id: string;
  title: string;
  createdAt: string;
  invitationId?: string | null;
  kolAcceptedAt?: string | null;
  signedAt?: string | null;
  signerName?: string | null;
  signatureType?: string | null;
  template?: { id: string; name: string } | null;
  invitation?: { id: string; kolProfile?: { name?: string } | null } | null;
};

type TemplateRow = { id: string; name: string; bodyMarkdown: string; variables?: unknown };

type InvitationOption = {
  id: string;
  status?: string;
  isBroadcasted?: boolean;
  kolProfile?: { name?: string } | null;
};

type ContractScope = 'per-kol' | 'job-wide';

const AUTO_KEYS = new Set(['jobTitle', 'brandName', 'corporateName', 'today', 'agencyName', 'kolName']);

function parseKeyValueLines(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  text.split('\n').forEach((line) => {
    const i = line.indexOf(':');
    if (i > 0) {
      const k = line.slice(0, i).trim();
      const v = line.slice(i + 1).trim();
      if (k) out[k] = v;
    }
  });
  return out;
}

function serializeKeyValueLines(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
}

function templateVariableHints(variables: unknown): Record<string, string> {
  if (!variables || typeof variables !== 'object' || Array.isArray(variables)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(variables as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

function sanitizeVars(text: string): Record<string, string> {
  const parsed = parseKeyValueLines(text);
  delete parsed.kolName;
  return parsed;
}

export default function JobContractsTab({
  jobId,
  agencyOrgId,
}: {
  jobId: string;
  agencyOrgId: string;
}) {
  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [invitations, setInvitations] = useState<InvitationOption[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [scope, setScope] = useState<ContractScope>('per-kol');
  const [invitationId, setInvitationId] = useState('');
  const [previewKolInvId, setPreviewKolInvId] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [extraVars, setExtraVars] = useState('feeRM:');
  const [previewBody, setPreviewBody] = useState('');
  const [previewUnresolved, setPreviewUnresolved] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [paymentModalContract, setPaymentModalContract] = useState<ContractRow | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDesc, setPaymentDesc] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId],
  );

  const broadcastedInvitations = useMemo(
    () => invitations.filter((inv) => inv.isBroadcasted),
    [invitations],
  );

  const selectedKolName = useMemo(() => {
    if (scope !== 'per-kol' || !invitationId) return null;
    const inv = invitations.find((i) => i.id === invitationId);
    return inv?.kolProfile?.name || null;
  }, [scope, invitationId, invitations]);

  const hintLines = useMemo(() => {
    const hints = templateVariableHints(selectedTemplate?.variables);
    return Object.entries(hints)
      .filter(([k]) => !AUTO_KEYS.has(k))
      .map(([k, desc]) => `${k}: (${desc})`);
  }, [selectedTemplate]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const hints = templateVariableHints(selectedTemplate.variables);
    const current = parseKeyValueLines(extraVars);
    const next = { ...current };
    let changed = false;
    for (const key of Object.keys(hints)) {
      if (AUTO_KEYS.has(key)) continue;
      if (next[key] === undefined) {
        next[key] = '';
        changed = true;
      }
    }
    if (changed) setExtraVars(serializeKeyValueLines(next));
  }, [templateId]);

  const load = useCallback(async () => {
    const [c, t, inv] = await Promise.all([
      api.get(`/jobs/${jobId}/contracts`),
      api.get('/contracts/templates', { params: { organizationId: agencyOrgId } }),
      api.get('/invitations', { params: { jobId } }),
    ]);
    setContracts(c.data);
    setTemplates(t.data);
    setInvitations(inv.data || []);
  }, [jobId, agencyOrgId]);

  useEffect(() => {
    if (jobId && agencyOrgId) load();
  }, [jobId, agencyOrgId, load]);

  const runPreview = useCallback(async () => {
    if (!templateId) {
      alert('Select a template first');
      return;
    }
    setPreviewLoading(true);
    try {
      const vars = sanitizeVars(extraVars);
      const payload: Record<string, unknown> = { templateId, variables: vars };
      if (scope === 'per-kol' && invitationId) {
        payload.invitationId = invitationId;
      } else if (scope === 'job-wide' && previewKolInvId) {
        payload.previewInvitationId = previewKolInvId;
      }
      const res = await api.post(`/jobs/${jobId}/contracts/preview`, payload);
      setPreviewBody(res.data.renderedBody || '');
      setPreviewUnresolved(res.data.unresolvedPlaceholders || []);
    } catch (e) {
      console.error(e);
      alert('Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [templateId, extraVars, scope, invitationId, previewKolInvId, jobId]);

  useEffect(() => {
    if (!templateId) {
      setPreviewBody('');
      setPreviewUnresolved([]);
      return;
    }
    const t = setTimeout(() => {
      runPreview();
    }, 400);
    return () => clearTimeout(t);
  }, [templateId, invitationId, previewKolInvId, extraVars, jobId, scope]);

  const generateSingle = async () => {
    if (!templateId) {
      alert('Select a template');
      return;
    }
    if (!invitationId) {
      alert('Select a KOL invitation');
      return;
    }
    if (previewUnresolved.length > 0) {
      const ok = confirm(
        `The preview still has empty placeholders: ${previewUnresolved.join(', ')}. Generate anyway?`,
      );
      if (!ok) return;
    }
    setGenerating(true);
    try {
      await api.post(`/jobs/${jobId}/contracts/generate`, {
        templateId,
        invitationId,
        title: customTitle.trim() || undefined,
        variables: sanitizeVars(extraVars),
      });
      setCustomTitle('');
      load();
    } finally {
      setGenerating(false);
    }
  };

  const generateBatch = async () => {
    if (!templateId) {
      alert('Select a template');
      return;
    }
    if (broadcastedInvitations.length === 0) {
      alert('No KOL invitations have been sent on this job yet.');
      return;
    }
    const ok = confirm(
      `This will generate a separate contract for each of the ${broadcastedInvitations.length} invited KOL(s). Continue?`,
    );
    if (!ok) return;
    setGenerating(true);
    try {
      await api.post(`/jobs/${jobId}/contracts/generate-batch`, {
        templateId,
        title: customTitle.trim() || undefined,
        variables: sanitizeVars(extraVars),
      });
      setCustomTitle('');
      load();
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <Card>
          <CardHeader>
            <CardTitle>Generate from template</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Choose template…</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </Select>

            <div>
              <label className="block text-xs font-medium text-glass-text-2 mb-1">
                Contract scope
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer text-glass-text">
                  <input
                    type="radio"
                    name="contract-scope"
                    value="per-kol"
                    checked={scope === 'per-kol'}
                    onChange={() => { setScope('per-kol'); setPreviewKolInvId(''); }}
                    className="accent-accent2"
                  />
                  Per-KOL
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer text-glass-text">
                  <input
                    type="radio"
                    name="contract-scope"
                    value="job-wide"
                    checked={scope === 'job-wide'}
                    onChange={() => { setScope('job-wide'); setInvitationId(''); }}
                    className="accent-accent2"
                  />
                  Job-wide (all invited KOLs)
                </label>
              </div>
            </div>

            {scope === 'per-kol' && (
              <div>
                <label className="block text-xs font-medium text-glass-text-2 mb-1">
                  KOL invitation
                </label>
                <Select value={invitationId} onChange={(e) => setInvitationId(e.target.value)}>
                  <option value="">Select a KOL…</option>
                  {invitations.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.kolProfile?.name || inv.id.slice(0, 8)}
                      {inv.status ? ` (${inv.status.toLowerCase()})` : ''}
                    </option>
                  ))}
                </Select>
                {selectedKolName && (
                  <p className="text-xs text-emerald-500 dark:text-emerald-300 mt-1">
                    <code className="bg-emerald-500/15 border border-emerald-500/30 px-1 rounded">kolName</code> will be auto-filled
                    as <strong>{selectedKolName}</strong>
                  </p>
                )}
              </div>
            )}

            {scope === 'job-wide' && (
              <div className="text-xs text-glass-text-2 rounded-xl border border-accent2/30 bg-accent2/10 p-2">
                {broadcastedInvitations.length === 0 ? (
                  <span>No KOL invitations have been sent yet. Broadcast invitations first, then generate contracts here.</span>
                ) : (
                  <span>
                    Will generate <strong className="text-glass-text">{broadcastedInvitations.length}</strong> contract(s) — one per invited KOL:{' '}
                    {broadcastedInvitations.map((inv) => inv.kolProfile?.name || inv.id.slice(0, 8)).join(', ')}.
                    Each contract&apos;s <code className="bg-accent2/15 border border-accent2/30 px-1 rounded">kolName</code> is
                    resolved automatically.
                  </span>
                )}
              </div>
            )}

            <Input
              placeholder="Optional custom title"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
            />
            <div>
              <label className="block text-xs font-medium text-glass-text-2 mb-1">
                Template variables (one per line)
              </label>
              <p className="text-xs text-glass-text-3 mb-2">
                Each line is <code className="bg-glass-3 border border-glass px-1 rounded">placeholderName: value</code>. These
                map to <code className="bg-glass-3 border border-glass px-1 rounded">{'{{placeholderName}}'}</code> in the
                template. Auto-filled:{' '}
                <code className="bg-glass-3 border border-glass px-1 rounded">jobTitle</code>,{' '}
                <code className="bg-glass-3 border border-glass px-1 rounded">brandName</code>,{' '}
                <code className="bg-glass-3 border border-glass px-1 rounded">today</code>,{' '}
                <code className="bg-glass-3 border border-glass px-1 rounded">agencyName</code>,{' '}
                <code className="bg-glass-3 border border-glass px-1 rounded">kolName</code>.
              </p>
              {hintLines.length > 0 && (
                <p className="text-xs text-amber-500 dark:text-amber-300 rounded-xl border border-amber-400/30 bg-amber-400/10 p-2 mb-2">
                  This template also expects: {hintLines.join(' · ')}
                </p>
              )}
              <Textarea
                rows={6}
                className="font-mono text-xs"
                placeholder={'feeRM: 5000\ndeliverablesSummary: 2 Instagram Reels, posting dates TBD'}
                value={extraVars}
                onChange={(e) => setExtraVars(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={runPreview} disabled={!templateId || previewLoading}>
                {previewLoading ? 'Updating preview…' : 'Refresh preview'}
              </Button>
              {scope === 'per-kol' ? (
                <Button type="button" onClick={generateSingle} disabled={!templateId || !invitationId || generating}>
                  {generating ? 'Generating…' : 'Generate contract'}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={generateBatch}
                  disabled={!templateId || broadcastedInvitations.length === 0 || generating}
                >
                  {generating ? 'Generating…' : `Generate for all invited KOLs (${broadcastedInvitations.length})`}
                </Button>
              )}
            </div>
            {previewUnresolved.length > 0 && (
              <p className="text-xs text-amber-500 dark:text-amber-300">
                Still empty in preview:{' '}
                {previewUnresolved.map((k) => (
                  <code key={k} className="bg-amber-400/15 border border-amber-400/30 px-1 rounded mr-1">
                    {`{{${k}}}`}
                  </code>
                ))}
                — add a line above or link an invitation where applicable.
              </p>
            )}
            {templates.length === 0 && (
              <p className="text-xs text-glass-text-3">
                No templates yet. Add some under Contract templates.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="xl:sticky xl:top-4">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle>Preview</CardTitle>
            {scope === 'job-wide' && broadcastedInvitations.length > 0 && (
              <Select
                value={previewKolInvId}
                onChange={(e) => setPreviewKolInvId(e.target.value)}
                className="w-auto min-w-[160px] text-sm"
              >
                <option value="">Preview as KOL…</option>
                {broadcastedInvitations.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.kolProfile?.name || inv.id.slice(0, 8)}
                  </option>
                ))}
              </Select>
            )}
          </CardHeader>
          <CardContent>
            {!templateId ? (
              <p className="text-sm text-glass-text-3">Select a template to see the filled text.</p>
            ) : previewLoading && !previewBody ? (
              <p className="text-sm text-glass-text-3">Loading preview…</p>
            ) : (
              <div className="max-h-[min(70vh,520px)] overflow-y-auto glass-scroll rounded-xl border border-glass p-4 bg-glass-2 text-glass-text whitespace-pre-wrap text-sm leading-relaxed">
                {previewBody || '—'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contract history</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-glass-text-3 mb-3">
            For all jobs at a glance, use <strong className="font-medium text-glass-text-2">Contracts overview</strong> in the sidebar.
          </p>
          <ul className="space-y-2 text-sm">
            {contracts.map((c) => {
              const kolName = c.invitation?.kolProfile?.name;
              return (
                <li key={c.id} className="flex flex-wrap justify-between gap-2 border-b border-glass pb-2">
                  <div>
                    <div className="font-medium flex flex-wrap items-center gap-2 text-glass-text">
                      {c.title}
                      {c.signedAt && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-500 dark:text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5">
                          Signed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-glass-text-3">
                      {c.template?.name || 'Custom'} · {new Date(c.createdAt).toLocaleString()}
                      {kolName ? ` · ${kolName}` : c.invitationId ? ' · Linked to invitation' : ' · Job-wide'}
                      {c.signedAt
                        ? ` · Signed by ${c.signerName || 'KOL'} on ${new Date(c.signedAt).toLocaleString()}`
                        : c.kolAcceptedAt
                          ? ` · KOL acknowledged ${new Date(c.kolAcceptedAt).toLocaleString()}`
                          : ''}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {c.invitationId && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-emerald-500 dark:text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10"
                        onClick={() => {
                          setPaymentModalContract(c);
                          setPaymentAmount('');
                          setPaymentDesc(`Payment for contract: ${c.title}`);
                        }}
                      >
                        + Payment
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const res = await api.get(`/contracts/${c.id}/pdf`, { responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `contract-${c.id.slice(0, 8)}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      PDF
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!confirm('Remove this contract record?')) return;
                        await api.delete(`/jobs/${jobId}/contracts/${c.id}`);
                        load();
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </li>
              );
            })}
            {contracts.length === 0 && <li className="text-glass-text-3">No contracts yet.</li>}
          </ul>
        </CardContent>
      </Card>

      <Modal
        isOpen={!!paymentModalContract}
        onClose={() => setPaymentModalContract(null)}
        title="Generate Payment from Contract"
      >
        <div className="space-y-4">
          <p className="text-sm text-glass-text-2">
            Create a payment record for KOL{' '}
            {paymentModalContract?.invitation?.kolProfile?.name || 'from contract'}: <em>{paymentModalContract?.title}</em>
          </p>
          <div>
            <label className="block text-sm font-medium text-glass-text-2 mb-1">
              Amount (RM)
            </label>
            <Input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="5000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-glass-text-2 mb-1">
              Description (optional)
            </label>
            <Input
              value={paymentDesc}
              onChange={(e) => setPaymentDesc(e.target.value)}
              placeholder="Payment for contract..."
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setPaymentModalContract(null)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!paymentAmount || parseInt(paymentAmount, 10) <= 0) {
                  alert('Please enter a valid amount');
                  return;
                }
                setPaymentLoading(true);
                try {
                  await api.post(`/contracts/${paymentModalContract!.id}/generate-payment`, {
                    amountRM: parseInt(paymentAmount, 10),
                    description: paymentDesc || undefined,
                  });
                  alert('Payment record created successfully');
                  setPaymentModalContract(null);
                } catch (e: unknown) {
                  const err = e as { response?: { data?: { error?: string } } };
                  alert(err.response?.data?.error || 'Failed to create payment');
                } finally {
                  setPaymentLoading(false);
                }
              }}
              disabled={paymentLoading}
            >
              {paymentLoading ? 'Creating...' : 'Create Payment'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
